import { useEffect, useMemo, useRef, useState } from "react";

export type Broker = { id: number; name: string };

type Props = {
  brokers: Broker[];
  selectedIds: number[];
  onChange: (next: number[]) => void;
  loading?: boolean;
};

export default function BrokerMultiSelect({
  brokers,
  selectedIds,
  onChange,
  loading,
}: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (term === "") return brokers;
    return brokers.filter((b) => b.name.toLowerCase().includes(term));
  }, [brokers, search]);

  const allSelected =
    brokers.length > 0 && selectedIds.length === brokers.length;
  const noneSelected = selectedIds.length === 0;

  const triggerLabel = (() => {
    if (loading) return "Cargando brokers…";
    if (brokers.length === 0) return "Sin brokers";
    if (allSelected || noneSelected) return `Todos (${brokers.length})`;
    return `${selectedIds.length} de ${brokers.length}`;
  })();

  const toggle = (id: number) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const selectAll = () => onChange(brokers.map((b) => b.id));
  const clearAll = () => onChange([]);

  return (
    <div
      ref={wrapperRef}
      style={{ position: "relative", display: "inline-block" }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={loading || brokers.length === 0}
        title="Filtrar por broker"
        style={{
          padding: "0.4rem 0.7rem",
          borderRadius: "0.4rem",
          background: open ? "#0069d9" : "white",
          color: open ? "white" : "#1f2933",
          border: open ? "1px solid #0069d9" : "1px solid #dee2e6",
          fontSize: ".82rem",
          fontWeight: 600,
          cursor: loading ? "wait" : "pointer",
          minWidth: 160,
          textAlign: "left",
          whiteSpace: "nowrap",
        }}
      >
        Broker · {triggerLabel}
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            zIndex: 30,
            background: "white",
            border: "1px solid #dee2e6",
            borderRadius: "0.5rem",
            boxShadow: "0 6px 16px rgba(0,0,0,.12)",
            width: 280,
            maxHeight: 360,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              padding: "0.55rem 0.7rem",
              borderBottom: "1px solid #f1f3f5",
              display: "flex",
              flexDirection: "column",
              gap: "0.45rem",
            }}
          >
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar broker…"
              autoFocus
              style={{
                padding: "0.35rem 0.55rem",
                borderRadius: "0.35rem",
                border: "1px solid #dee2e6",
                background: "#f8f9fb",
                fontSize: ".82rem",
              }}
            />
            <div
              style={{
                display: "flex",
                gap: "0.4rem",
                fontSize: ".72rem",
              }}
            >
              <button
                type="button"
                onClick={selectAll}
                style={linkBtn}
              >
                Seleccionar todos
              </button>
              <button
                type="button"
                onClick={clearAll}
                style={linkBtn}
              >
                Quitar todos
              </button>
            </div>
          </div>
          <div style={{ overflowY: "auto", flex: 1, padding: "0.25rem 0" }}>
            {filtered.length === 0 ? (
              <div
                style={{
                  padding: "0.5rem 0.7rem",
                  fontSize: ".8rem",
                  color: "#73879C",
                }}
              >
                Sin coincidencias
              </div>
            ) : (
              filtered.map((b) => {
                const checked = selectedIds.includes(b.id);
                return (
                  <label
                    key={b.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      padding: "0.35rem 0.7rem",
                      cursor: "pointer",
                      fontSize: ".82rem",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#f8f9fb";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(b.id)}
                    />
                    <span>{b.name}</span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const linkBtn: React.CSSProperties = {
  padding: 0,
  border: "none",
  background: "transparent",
  color: "#0069d9",
  fontWeight: 600,
  cursor: "pointer",
  fontSize: ".72rem",
};
